import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Modal } from '@/components/ui/modal';
import { normalizeGatewayEui } from '@/lib/eui';
import { assignGatewayToProperty } from '@/features/gateways/api/gateways-api';
import type { Gateway } from '@/types/api';

interface AssignGatewayModalProps {
  propertyId: string;
  onClose: () => void;
  onAssigned: (gateway: Gateway) => void;
}

export function AssignGatewayModal({ propertyId, onClose, onAssigned }: AssignGatewayModalProps) {
  const [rawEui, setRawEui] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const { value, valid } = normalizeGatewayEui(rawEui);
    if (!value) {
      setFieldError('Gateway EUI is required.');
      return;
    }
    if (!valid) {
      setFieldError('Gateway EUI must be 16 hexadecimal characters.');
      return;
    }
    setFieldError(null);
    setSubmitError(null);
    setIsSubmitting(true);
    try {
      const res = await assignGatewayToProperty(propertyId, value);
      if (res.success && res.data) {
        onAssigned(res.data);
      } else {
        setSubmitError(res.message ?? 'Failed to assign gateway.');
      }
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setSubmitError(message ?? 'Failed to assign gateway. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Modal
      isOpen
      onClose={onClose}
      title="Assign Gateway"
      description="Link a gateway to this property by entering its EUI."
      size="sm"
    >
      <form noValidate onSubmit={handleSubmit} className="space-y-4">
        <Input
          label="Gateway EUI"
          required
          autoFocus
          value={rawEui}
          onChange={(e) => { setRawEui(e.target.value.toUpperCase()); setFieldError(null); setSubmitError(null); }}
          placeholder="e.g., AABBCCDDEEFF0011"
          error={fieldError ?? undefined}
          helperText="16 hex characters from the gateway's label. Separators (: or -) are accepted."
        />

        {submitError && (
          <p className="text-sm text-error-600">{submitError}</p>
        )}

        <div className="flex gap-3 pt-2">
          <Button type="button" variant="outline" onClick={onClose} className="flex-1" disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" isLoading={isSubmitting} className="flex-1">
            Assign Gateway
          </Button>
        </div>
      </form>
    </Modal>
  );
}
